import { CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const brands = [
  { name: 'Optimum Nutrition', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/s4i4bqjo_IMG_2460.png', hasImage: true },
  { name: 'MuscleBlaze', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/0e0wj5zv_IMG_2461.jpeg', hasImage: true },
  { name: 'MyProtein', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/v24eariy_IMG_2463.jpeg', hasImage: true },
  { name: 'GAT Sport', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/qhjyx08h_IMG_2465.jpeg', hasImage: true },
  { name: 'Kevin Levrone', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/n2uhsfh7_IMG_2473.jpeg', hasImage: true },
  { name: 'MuscleTech', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/7a5vdsjv_IMG_2472.png', hasImage: true },
  { name: 'Rule One', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/p5cc9er1_IMG_2471.png', hasImage: true },
  { name: 'Dymatize', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/7iwizj1l_IMG_2468.png', hasImage: true },
  { name: 'Cellucor', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/udt4xkul_IMG_2466.png', hasImage: true },
  { name: 'Ronnie Coleman', logo: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/9n51x04o_IMG_2474.png', hasImage: true },
];

export const BrandsSection = () => {
  return (
    <section className="py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #1a0a20 0%, #2d1035 50%, #1a0a20 100%)' }}>
      {/* Premium Background Elements */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[200px] animate-pulse-soft" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gold/15 rounded-full blur-[180px] animate-pulse-soft" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-0 w-[300px] h-[300px] bg-gold/10 rounded-full blur-[150px]" />
      <div className="absolute top-1/3 right-0 w-[250px] h-[250px] bg-primary/15 rounded-full blur-[120px]" />
      
      {/* Floating Decorative Elements */}
      <div className="absolute top-20 left-16 w-4 h-4 border border-gold/30 rounded-full animate-float" />
      <div className="absolute bottom-32 right-20 w-3 h-3 bg-gold/20 rounded-full animate-float" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 right-12 w-5 h-5 border border-primary/20 rounded-full animate-float" style={{ animationDelay: '2s' }} />
      <div className="absolute bottom-1/4 left-24 w-2 h-2 bg-primary/30 rounded-full animate-float" style={{ animationDelay: '0.5s' }} />

      {/* Grain Texture */}
      <div className="absolute inset-0 texture-grain opacity-50" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Premium Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-3 text-gold font-semibold text-sm uppercase tracking-wider mb-4">
            <Sparkles className="w-4 h-4" />
            <span className="w-12 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
            PREMIUM PARTNERS
            <span className="w-12 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
            <Sparkles className="w-4 h-4" />
          </span>
          <h2 className="text-4xl sm:text-5xl font-display font-bold text-white mb-4">
            World-Class <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold via-yellow-400 to-gold">Brands</span> We Offer
          </h2>
          <p className="text-base text-white/60 max-w-2xl mx-auto">
            All products are directly sourced from authorized distributors. 100% genuine guaranteed.
          </p>
        </div>

        {/* Premium Brands Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
          {brands.map((brand, index) => (
            <div
              key={brand.name}
              className="group relative rounded-2xl p-5 md:p-6 bg-white/5 backdrop-blur-md border border-white/10 hover:border-gold/40 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-gold/10 animate-fade-up"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Hover Glow Effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-gold/5 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Top Accent Line */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-gold via-primary to-gold group-hover:w-3/4 transition-all duration-500 rounded-full" />
              
              <div className="flex flex-col items-center text-center relative z-10">
                {brand.hasImage ? (
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden mb-4 bg-white flex items-center justify-center shadow-lg shadow-black/20 group-hover:shadow-xl group-hover:shadow-gold/20 transition-all duration-500 group-hover:scale-105">
                    <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain p-2" />
                  </div>
                ) : (
                  <div className={`w-20 h-20 md:w-24 md:h-24 rounded-xl ${brand.color} flex items-center justify-center mb-4`}>
                    <span className="text-2xl font-bold text-primary-foreground">{brand.logo}</span>
                  </div>
                )}
                <h4 className="font-semibold text-white text-sm group-hover:text-gold transition-colors duration-300">{brand.name}</h4>
                <span className="inline-flex items-center gap-1 text-xs text-green-400 font-medium mt-2 px-2 py-0.5 bg-green-500/10 rounded-full border border-green-500/20">
                  <CheckCircle className="w-3 h-3" strokeWidth={2} />
                  Authorized
                </span>
              </div>
              
              {/* Premium Corner Badge */}
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-gradient-to-br from-gold/80 to-gold flex items-center justify-center shadow-lg shadow-gold/30 group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="w-4 h-4 text-white" strokeWidth={2} />
              </div>
            </div>
          ))}
        </div>

        {/* Premium Request Brand CTA */}
        <div className="text-center mt-14">
          <div className="inline-flex flex-col items-center gap-4 p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
            <p className="text-white/70">Can&apos;t find your favorite brand?</p>
            <Button 
              className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-black font-semibold px-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-gold/30"
              asChild
            >
              <a href="https://wa.me/9779824042067?text=Hi!%20I%27m%20looking%20for%20a%20specific%20brand" target="_blank" rel="noopener noreferrer">
                <Sparkles className="w-4 h-4 mr-2" />
                Request a Brand
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
    </section>
  );
};
